import { useQuery } from "@tanstack/react-query";
import { startTransition, useEffect } from "react";
import { CollapsibleState } from "~/components/build/Show/pages/steps/list/components/TreeItem/CollapsibleState";
import {
  TreeNode,
  TreeItemContext,
  createStepNode,
} from "~/components/build/Show/pages/steps/list/components/TreeNode";
import { STEP_ITEM_HEIGHT } from "~/components/build/Show/pages/steps/list/components/constants";
import { CollapsibleStepItem } from "~/components/build/Show/pages/steps/list/components/TreeNode/CollapsibleStepItem";
import {
  FilterByOption,
  filterSteps,
  sortSteps,
  stepDirectlyMatchesFilters,
} from "~/components/build/Show/lib/useFilterStore";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { Build } from "~/components/build/Show/lib/types";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { GroupStep, Step, isFailed } from "~/lib/pipeline";
import { childrenStepsQueryOptions } from "~/components/build/Show/lib/queries/steps";

const GroupStepItem = ({ step, ...props }: { step: GroupStep } & TreeItemContext) => {
  const { build } = useBuild();
  const setSteps = useBuildStore((state) => state.setSteps);
  const stepsGroupedByParent = useBuildStore((state) => state.stepsGroupedByParent);

  const query = useQuery(
    childrenStepsQueryOptions(build, step.uuid, {
      enabled:
        props.collapsibleState === CollapsibleState.Expanded ||
        props.collapsibleState === CollapsibleState.Full,
    }),
  );

  useEffect(() => {
    if (query.data) {
      startTransition(() => {
        setSteps(query.data, build);
      });
    }
  }, [query.data]);

  // Show loading state when initial fetch is in progress and we haven't populated the store with all the children.
  const showLoadingState =
    query.isLoading &&
    stepsGroupedByParent.get(step.uuid)?.length !== step.statistics?.stepChildrenCount;

  return <CollapsibleStepItem {...props} step={step} isLoading={showLoadingState} />;
};

export const createGroupStepNode = (step: GroupStep, build: Build): TreeNode => ({
  id: step.uuid,
  height: STEP_ITEM_HEIGHT,
  sticky: true,
  render: (props) => <GroupStepItem step={step} {...props} />,
  childNodes: (collapsibleState: CollapsibleState, filters: FilterByOption[]) => {
    if (collapsibleState === CollapsibleState.Expanded) {
      const steps: Step[] = useBuildStore.getState().stepsGroupedByParent.get(step.uuid) || [];
      let filteredSteps = filterSteps(steps, filters, build);
      filteredSteps = sortSteps(filteredSteps);

      return filteredSteps.map((step) => createStepNode(step, build));
    }
    return [];
  },
});

/**
 * Create a group step node scoped to a specific state section.
 *
 * Used in the state-grouped view (DisplayMode.State) where a group may appear
 * under multiple state headers. Children are filtered to only those matching
 * the given state. The node ID is namespaced to avoid collisions when the same
 * group appears in multiple state sections.
 */
export const createGroupStepNodeForState = (
  step: GroupStep,
  build: Build,
  stateFilter: FilterByOption,
): TreeNode => {
  // When the group doesn't directly match the state it's listed under, it's
  // only present because of its children. Auto-expand so users can see which
  // children belong to this state section. Also always expand failed groups
  // since they're the most actionable.
  const groupMatchesState = stepDirectlyMatchesFilters(step, [stateFilter], build);
  const shouldAutoExpand = !groupMatchesState || isFailed(step);

  return {
    id: `${step.uuid}_${stateFilter}`,
    height: STEP_ITEM_HEIGHT,
    sticky: true,
    deemphasized: !groupMatchesState,
    defaultCollapsibleState: shouldAutoExpand
      ? CollapsibleState.Expanded
      : CollapsibleState.Collapsed,
    render: (props) => <CollapsibleStepItem step={step} {...props} />,
    childNodes: (collapsibleState: CollapsibleState) => {
      if (collapsibleState === CollapsibleState.Expanded) {
        const steps: Step[] = useBuildStore.getState().stepsGroupedByParent.get(step.uuid) || [];
        let filteredSteps = filterSteps(steps, [stateFilter], build);
        filteredSteps = sortSteps(filteredSteps);

        return filteredSteps.map((childStep) => createStepNode(childStep, build));
      }
      return [];
    },
  };
};
